/**
 * Browser file-download helpers — centralize the synthetic-anchor click that
 * several export/artifact paths each open-coded.
 */

/** How long a download frame lingers before it is torn down. */
const FRAME_TTL_MS = 120_000;

export interface DownloadUrlOptions {
  /**
   * Called when a cross-origin download demonstrably did not produce a file.
   * Never called on the same-origin path, and never called for a download that
   * started normally.
   *
   * Cross-origin `<iframe>` `load`/`error` events don't reliably distinguish
   * *why* — a blocked request (ad blocker, extension, proxy) commonly fires
   * `load`, the same event a rendered S3 XML error page fires, rather than
   * `error`. Reporting them as two different reasons let the UI tell a user
   * whose download was actually blocked to just "mint a fresh link," which
   * fails the same way again. One reason, one message that covers both.
   */
  onBlocked?: () => void;
}

function isSameOrigin(url: string): boolean {
  // Object and data URLs are minted by this document and are always ours; some
  // engines report an opaque origin for them, so short-circuit rather than
  // depending on how URL parsing reports it.
  if (url.startsWith("blob:") || url.startsWith("data:")) return true;
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    // Unparseable — take the frame path, which can never replace this tab.
    return false;
  }
}

/**
 * Download an already-resolved URL (e.g. a signed artifact URL).
 *
 * Same-origin and object URLs use a synthetic anchor, where the `download`
 * attribute is honored and supplies the filename.
 *
 * A CROSS-ORIGIN url — every presigned S3 artifact URL this app downloads — is
 * loaded into a hidden iframe instead. Browsers ignore `download` cross-origin,
 * so an anchor click there degrades into a plain top-level navigation: the
 * portal tab leaves for the S3 host, and anything that aborts that request (an
 * ad blocker, an extension, an enterprise policy, a corporate proxy) replaces
 * the entire app with a browser error page. That is BIO-308 #3 — Chrome showed
 * `ERR_BLOCKED_BY_CLIENT` and the portal was gone.
 *
 * A frame navigation cannot replace this tab, and the backend already presigns
 * with `Content-Disposition: attachment` (`artifacts/signer.py`), so the frame
 * load turns into a file download and the server supplies the filename.
 *
 * `onBlocked` fires on either a `load` (the frame rendered a document instead
 * of downloading — an expired/denied link, or several blockers) or an `error`
 * (the request never completed) — the two aren't a reliable signal of which
 * actually happened, so both are reported the same way. A download that starts
 * normally aborts the frame navigation, so no event arrives on the happy path.
 */
export function downloadUrl(url: string, filename: string, options: DownloadUrlOptions = {}): void {
  if (isSameOrigin(url)) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    return;
  }

  const frame = document.createElement("iframe");
  frame.style.display = "none";
  frame.setAttribute("aria-hidden", "true");
  // Deliberately NOT sandboxed: `allow-downloads` only reaches Safari 16.4+, and
  // an older engine ignores the unknown token while honoring the sandbox — which
  // would block the very download this frame exists to perform. The URL is one
  // this app just minted through its own backend, so the frame adds no exposure
  // the anchor it replaces did not already have.

  let timer = 0;
  let settled = false;
  /**
   * Tear the frame down, reporting a block unless this was the reaper.
   *
   * The `settled` guard covers the callback as well as the teardown: a frame
   * can emit more than one event, and reporting a single failed download twice
   * would show the user two contradictory notifications.
   */
  const finish = (blocked: boolean) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timer);
    frame.remove();
    if (blocked) options.onBlocked?.();
  };

  // Either event means the download didn't happen — see the doc comment above
  // for why `load` vs `error` isn't a reliable "why".
  frame.addEventListener("load", () => finish(true));
  frame.addEventListener("error", () => finish(true));

  // The happy path emits no event at all — the download aborts the frame
  // navigation — so the reaper is what cleans up after a SUCCESSFUL download
  // and must not report anything.
  timer = window.setTimeout(() => finish(false), FRAME_TTL_MS);
  // src before append: the navigation begins on insertion, so there is no
  // separate about:blank load event to mistake for a failure.
  frame.src = url;
  document.body.appendChild(frame);
}

/** Download a Blob via a temporary object URL (revoked after the click). */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  downloadUrl(url, filename);
  // Defer the revoke off the current tick: revoking in the same synchronous
  // tick as the synthetic click can cancel the download before the browser has
  // read the blob, producing an intermittent zero-byte file (BIO-144 #8). One
  // macrotask is enough; the brief object-URL lifetime is negligible.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
